/**
 * Flow sessions and AI "process" UI for canvas nodes: dispatch chat / action runs,
 * aggregate tray, node highlights, and persistence in node `data`. Mount under
 * `CanvasLoroProvider` + `CanvasRouteProvider` (e.g. via `CanvasProvider`); reads
 * `canvasId` and Loro nodes from context.
 */

import { useCanvasFlowSessions } from "@/api/canvas";
import {
  useCreateFlowSession,
  useDeleteFlowSession,
} from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import { flowSessionModeLabel } from "@/constants/flow-session-modes";
import { useEventStream } from "@/providers/EventStreamProvider";
import { EventStreamEvent } from "@/types/events";
import {
  FLOW_SESSION_MODES,
  type FlowSession,
  type FlowSessionMode,
  type ReferencedEntity,
} from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import {
  getEntityData,
  getEntityName,
  getEntityType,
} from "@cohesive/ui-components";
import { useEventListener } from "@liveblocks/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvasApi, useCanvasDoc } from "./CanvasContext";
import { useCanvasFloatingChat } from "./CanvasFloatingChatContext";
import { useCanvasRoute } from "./CanvasRouteContext";

// =============================================================================
// Persisted Process Metadata (stored on node data)
// =============================================================================

interface PersistedProcessInfo {
  flowSessionId: string;
  label: string;
}

// =============================================================================
// External event: a flow session was deleted out-of-band (e.g. from the
// CanvasExplorer chats list). Anyone holding state derived from a flow
// session — the tray, persisted `processInfo` on nodes, and any
// `chatSession` node placed by quick-chat — listens to this and tears
// down its local copy. Going through a window event keeps the explorer
// pane decoupled from the canvas page's React tree (they don't share a
// provider), and it survives the canvas not yet being mounted (the
// underlying API delete is the source of truth either way).
// =============================================================================

export const CANVAS_FLOW_SESSION_DELETED_EVENT = "canvas:flow-session-deleted";

export function emitCanvasFlowSessionDeleted(flowSessionId: string) {
  window.dispatchEvent(
    new CustomEvent(CANVAS_FLOW_SESSION_DELETED_EVENT, {
      detail: { flowSessionId },
    }),
  );
}

// =============================================================================
// Process Types (duplicated from BaseCard for type safety)
// =============================================================================

export type ProcessStatus =
  | "pending"
  | "processing"
  | "complete"
  | "error"
  | "needs_attention";

export type Process = {
  /** Unique identifier for the process */
  id: string;
  /** Display label for the process */
  label: string;
  /** Current status */
  status: ProcessStatus;
  /** Custom content shown when expanded (ReactNode) */
  expandedContent?: React.ReactNode;
  /** Optional icon override */
  icon?: React.ReactNode;
  /** Optional click handler for the process indicator */
  onClick?: () => void;
  /** Associated flow session ID for persistence */
  flowSessionId?: string;
  /** Associated node ID */
  nodeId?: string;
  /** Timestamp when the process was created */
  createdAt?: number;
  /** If true, this process is hidden from the node but still visible in the tray */
  hiddenFromNode?: boolean;
};

interface AddProcessOptions {
  /** If true, marks existing processes on the node as hidden (they still appear in tray) */
  overwriteExisting?: boolean;
}

// =============================================================================
// Types
// =============================================================================

export type CanvasProcessAction = "chat" | "execute_action_item";

export interface DispatchProcessParams {
  nodeId: string;
  /** Entity for entity-type canvas nodes. Omit for canvas-native nodes (textNote, section, etc.). */
  entity?: Entity;
  /** Explicit referenced entities. When provided, overrides the entity-derived referenced_entities. Required when entity is omitted. */
  referencedEntities?: ReferencedEntity[];
  action: CanvasProcessAction;
  customPrompt?: string; // For chat action - user's custom message
}

interface CanvasProcessContextType {
  /** Get all processes for a specific node */
  getProcessesForNode: (nodeId: string) => Process[];
  /** Dispatch a new process (creates flow session + posts message). Returns the flow session ID. */
  dispatchProcess: (
    params: DispatchProcessParams,
  ) => Promise<string | undefined>;
  /** Check if a process is currently running for a node */
  isProcessing: (nodeId: string) => boolean;
  /** All processes across all nodes */
  allProcesses: Process[];
  /** Count of active (pending, processing, or needs_attention) processes */
  activeProcessCount: number;
  /** Whether processes are being loaded/rehydrated */
  isLoading: boolean;
  /** Node IDs to highlight on the canvas (e.g. when hovering a process in the tray) */
  highlightedNodeIds: Set<string>;
  /** Set node IDs to highlight, or null to clear */
  setHighlightedNodeIds: (nodeIds: string[] | null) => void;
  /** Remove a process from the tray (dismisses it from the list) */
  removeProcess: (processId: string) => void;
}

// =============================================================================
// Action Configuration
// =============================================================================

const ACTION_CONFIG: Record<
  CanvasProcessAction,
  {
    label: string;
    mode: FlowSessionMode;
    prompt: string;
  }
> = {
  chat: {
    label: "Thinking...",
    mode: FLOW_SESSION_MODES.CANVAS_CHAT,
    prompt: "", // Will be overridden by customPrompt
  },
  execute_action_item: {
    label: "Executing action item...",
    mode: FLOW_SESSION_MODES.CANVAS_CHAT,
    prompt: "Execute this action item.",
  },
};

// =============================================================================
// Helpers
// =============================================================================

/**
 * Map FlowSession status to ProcessStatus
 */
function mapFlowSessionStatus(status: FlowSession["status"]): ProcessStatus {
  switch (status) {
    case "initialized":
    case "processing":
      return "processing";
    case "ready":
      return "complete";
    case "deleted":
      return "complete";
    default:
      return "pending";
  }
}

// =============================================================================
// Context
// =============================================================================

const CanvasProcessContext = createContext<CanvasProcessContextType | null>(
  null,
);

/** Throws unless wrapped by `CanvasProcessProvider` (canvas page subtree). */
export function useCanvasProcess() {
  const context = useContext(CanvasProcessContext);
  if (!context) {
    throw new Error(
      "useCanvasProcess must be used within CanvasProcessProvider",
    );
  }
  return context;
}

const PUBLIC_VIEW_PROCESS_VALUE: CanvasProcessContextType = {
  getProcessesForNode: () => [],
  dispatchProcess: async () => undefined,
  isProcessing: () => false,
  allProcesses: [],
  activeProcessCount: 0,
  isLoading: false,
  highlightedNodeIds: new Set(),
  setHighlightedNodeIds: () => {},
  removeProcess: () => {},
};

/**
 * No-op process tray for the public canvas-view SPA (no Auth0 / API /
 * flow sessions). Same context shape so EntityNode can mount unchanged.
 */
export function CanvasProcessStubProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <CanvasProcessContext.Provider value={PUBLIC_VIEW_PROCESS_VALUE}>
      {children}
    </CanvasProcessContext.Provider>
  );
}

// =============================================================================
// Provider
// =============================================================================

/** Supplies flow-session state; reads `canvasId` from `CanvasRouteContext` and nodes from `CanvasLoroContext`. */
export function CanvasProcessProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { canvasId: routeCanvasId } = useCanvasRoute();
  const canvasId = routeCanvasId ?? "";
  const canvas = useCanvasApi();
  const { nodes: canvasNodes } = useCanvasDoc();
  const canvasEnvRef = useRef({ canvas, canvasNodes });
  canvasEnvRef.current = { canvas, canvasNodes };
  // Store processes by nodeId
  const nodeProcessesRef = useRef<Map<string, Process[]>>(new Map());
  // Store mapping of flowSessionId -> { nodeId, processId }
  const sessionMapRef = useRef<
    Map<string, { nodeId: string; processId: string }>
  >(new Map());
  // Track which flow sessions have been rehydrated to avoid duplicates
  const rehydratedSessionsRef = useRef<Set<string>>(new Set());
  // Map action_item_id -> nodeId for detecting sub-agent sessions
  const actionItemNodeMapRef = useRef<Map<string, string>>(new Map());
  // Version counter to trigger re-renders (used as dependency for memoized values)
  const [version, setVersion] = useState(0);

  // Node IDs highlighted when hovering a process in the tray
  const [highlightedNodeIds, setHighlightedNodeIdsState] = useState<
    Set<string>
  >(new Set());

  const setHighlightedNodeIds = useCallback((nodeIds: string[] | null) => {
    setHighlightedNodeIdsState((prev) => {
      if (!nodeIds || nodeIds.length === 0) {
        return prev.size === 0 ? prev : new Set();
      }
      const next = new Set(nodeIds);
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev;
      }
      return next;
    });
  }, []);

  const queryClient = useQueryClient();
  const { mutateAsync: createFlowSessionAsync } = useCreateFlowSession();
  const { mutate: deleteFlowSessionMutate } = useDeleteFlowSession({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["canvas-flow-sessions"],
      });
    },
  });
  const { mutateAsync: postFlowMessageAsync } = usePostFlowMessage();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();

  // Fetch flow sessions for this canvas (initial load only, WebSocket handles updates)
  const { data: flowSessions, isLoading: isLoadingFlowSessions } =
    useCanvasFlowSessions(canvasId);

  // WebSocket event subscription for real-time updates (legacy path, kept during cutover)
  const { registerHandler } = useEventStream();

  // Navigate to a flow session in the right pane
  const navigateToFlowSession = useCallback(
    (flowSessionId: string) => {
      openCanvasFloatingSession(flowSessionId);
    },
    [openCanvasFloatingSession],
  );

  // Force re-render when processes change
  const triggerUpdate = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  // Helper to add a new process to a node
  const addProcessToNode = useCallback(
    (
      nodeId: string,
      process: Omit<Process, "onClick"> & { flowSessionId: string },
      options?: AddProcessOptions,
    ) => {
      const processWithClick: Process = {
        ...process,
        createdAt: process.createdAt ?? Date.now(),
        onClick: () => navigateToFlowSession(process.flowSessionId),
      };

      const existingProcesses = nodeProcessesRef.current.get(nodeId) || [];

      // If overwriteExisting is true, mark existing processes as hidden from node
      // They will still appear in the tray but not under the node
      const updatedExistingProcesses = options?.overwriteExisting
        ? existingProcesses.map((p) => ({ ...p, hiddenFromNode: true }))
        : existingProcesses;

      nodeProcessesRef.current.set(nodeId, [
        ...updatedExistingProcesses,
        processWithClick,
      ]);

      // Store mapping
      sessionMapRef.current.set(process.flowSessionId, {
        nodeId,
        processId: process.id,
      });

      // Mark as tracked to avoid rehydration duplication
      rehydratedSessionsRef.current.add(process.flowSessionId);

      triggerUpdate();
    },
    [navigateToFlowSession, triggerUpdate],
  );

  // Liveblocks broadcast event listener for process.* events emitted by the Go
  // backend via liveblocksClient.BroadcastEvent (Phase 5 write path).
  useEventListener(({ event }) => {
    if (event.type === "process.step.started") {
      addProcessToNode(event.nodeId, {
        id: `${event.runId}-${event.nodeId}`,
        label: "Running…",
        status: "processing",
        flowSessionId: event.runId,
        nodeId: event.nodeId,
        createdAt: Date.now(),
      });
    } else if (
      event.type === "process.step.completed" ||
      event.type === "process.step.failed"
    ) {
      triggerUpdate();
    }
  });

  // =============================================================================
  // WebSocket: Subscribe to flow session events for real-time status changes
  // =============================================================================
  useEffect(() => {
    const handleEvent = (event: EventStreamEvent) => {
      // Keep the React Query cache for `["canvas-flow-sessions", canvasId]`
      // in lockstep with WebSocket events so list consumers (ChatsScope's
      // chats / agent-tasks lists, the floating chat menu, etc.) reflect
      // server-spawned sessions immediately. Without this, agent tasks
      // generated by another flow only show up after a manual refresh
      // because nothing else invalidates the cache.
      //
      // Scoped to events for *this* canvas (`canvas_id` on the payload)
      // so multi-canvas tab setups don't refetch every list on every
      // event.
      if (
        canvasId &&
        (event.type === "flow_session.created" ||
          event.type === "flow_session.updated") &&
        event.data.flow_session.canvas_id === canvasId
      ) {
        queryClient.invalidateQueries({
          queryKey: ["canvas-flow-sessions", canvasId],
        });
      }

      // Handle flow_session.created - detect sub-agent sessions for action items
      if (event.type === "flow_session.created") {
        const newSession = event.data.flow_session;
        const actionItemId = newSession.action_item_id;

        // When there is no canvas node (canvas_agent items are internal and
        // never placed on canvas) we create an orphan process using a stable
        // synthetic anchor key so it still appears in the tray.
        if (actionItemId) {
          const nodeId = actionItemNodeMapRef.current.get(actionItemId);

          // Don't add if we already have this session
          if (sessionMapRef.current.has(newSession.id)) return;

          const anchorId = nodeId ?? `orphan-${actionItemId}`;
          addProcessToNode(
            anchorId,
            {
              id: uuidv4(),
              label: flowSessionModeLabel(newSession.mode) || "Running",
              status: mapFlowSessionStatus(newSession.status),
              flowSessionId: newSession.id,
              nodeId, // undefined for orphan processes
              createdAt: newSession.created_at
                ? new Date(newSession.created_at).getTime()
                : Date.now(),
            },
            { overwriteExisting: !!nodeId },
          );
        }
        return;
      }

      // Handle flow_session.updated - update existing process status
      if (event.type === "flow_session.updated") {
        const updatedSession = event.data.flow_session;
        const mapping = sessionMapRef.current.get(updatedSession.id);

        if (!mapping) return; // Not a session we're tracking

        const processes = nodeProcessesRef.current.get(mapping.nodeId);
        if (!processes) return;

        const newStatus = mapFlowSessionStatus(updatedSession.status);
        const process = processes.find((p) => p.id === mapping.processId);

        if (process && process.status !== newStatus) {
          const updatedProcesses = processes.map((p) =>
            p.id === mapping.processId ? { ...p, status: newStatus } : p,
          );
          nodeProcessesRef.current.set(mapping.nodeId, updatedProcesses);
          triggerUpdate();
        }
      }
    };

    const unregister = registerHandler(handleEvent);
    return unregister;
  }, [registerHandler, triggerUpdate, addProcessToNode, canvasId, queryClient]);

  // =============================================================================
  // Rehydration: Rebuild process state from persisted flow sessions
  // =============================================================================
  useEffect(() => {
    if (!flowSessions || flowSessions.length === 0) return;

    // Build map of flowSessionId -> { nodeId, label? } from Loro node data
    // label is optional - if not present, we'll get it from the flow session API response
    const sessionToNodeInfo = new Map<
      string,
      { nodeId: string; label?: string }
    >();
    for (const node of canvasNodes) {
      // Support both old format (flowSessionIds: string[]) and new format (processInfo: PersistedProcessInfo[])
      const processInfo =
        (node.data?.processInfo as PersistedProcessInfo[]) || [];
      const legacyFlowSessionIds =
        (node.data?.flowSessionIds as string[]) || [];

      // New format with labels
      for (const info of processInfo) {
        sessionToNodeInfo.set(info.flowSessionId, {
          nodeId: node.id,
          label: info.label,
        });
      }

      // Legacy format (no label - will be resolved from API response)
      for (const sessionId of legacyFlowSessionIds) {
        if (!sessionToNodeInfo.has(sessionId)) {
          sessionToNodeInfo.set(sessionId, {
            nodeId: node.id,
            // No label - will use flow session's template name
          });
        }
      }
    }

    // Build a map of flowSessionId -> label from API response for fallback
    const sessionLabels = new Map<string, string>();
    for (const session of flowSessions) {
      const label =
        flowSessionModeLabel(session.mode) || session.name || "Process";
      sessionLabels.set(session.id, label);
    }

    let hasChanges = false;

    // Rebuild/update process state from flow sessions
    for (const session of flowSessions) {
      const nodeInfo = sessionToNodeInfo.get(session.id);

      // Skip sessions that aren't linked to any node on this canvas
      if (!nodeInfo) continue;

      // Get label: prefer persisted label, fallback to API response
      const label =
        nodeInfo.label || sessionLabels.get(session.id) || "Process";

      // Check if we already have this session in our state
      const existingMapping = sessionMapRef.current.get(session.id);

      if (existingMapping) {
        // Update existing process status
        const processes = nodeProcessesRef.current.get(existingMapping.nodeId);
        if (processes) {
          const newStatus = mapFlowSessionStatus(session.status);
          const process = processes.find(
            (p) => p.id === existingMapping.processId,
          );
          if (process && process.status !== newStatus) {
            const updatedProcesses = processes.map((p) =>
              p.id === existingMapping.processId
                ? { ...p, status: newStatus }
                : p,
            );
            nodeProcessesRef.current.set(
              existingMapping.nodeId,
              updatedProcesses,
            );
            hasChanges = true;
          }
        }
      } else if (!rehydratedSessionsRef.current.has(session.id)) {
        // Create new process from rehydrated session
        rehydratedSessionsRef.current.add(session.id);

        const processId = uuidv4();
        const newProcess: Process = {
          id: processId,
          label, // Use persisted label or fallback to flow session template name
          status: mapFlowSessionStatus(session.status),
          flowSessionId: session.id,
          nodeId: nodeInfo.nodeId,
          createdAt: session.created_at
            ? new Date(session.created_at).getTime()
            : Date.now(),
          onClick: () => navigateToFlowSession(session.id),
        };

        // Add to node processes
        const existingProcesses =
          nodeProcessesRef.current.get(nodeInfo.nodeId) || [];
        nodeProcessesRef.current.set(nodeInfo.nodeId, [
          ...existingProcesses,
          newProcess,
        ]);

        // Store mapping
        sessionMapRef.current.set(session.id, {
          nodeId: nodeInfo.nodeId,
          processId,
        });
        hasChanges = true;
      }
    }

    // ==========================================================================
    // Second pass: Rehydrate sub-agent sessions (those with action_item_id)
    // ==========================================================================
    // Build a map of action_item_id -> nodeId from entity data in nodes
    const actionItemToNodeMap = new Map<string, string>();
    for (const node of canvasNodes) {
      const entity = node.data?.entity as
        | { base?: string; data?: { id?: string } }
        | undefined;
      if (entity?.base === "action_item" && entity?.data?.id) {
        actionItemToNodeMap.set(String(entity.data.id), node.id);
      }
    }

    // Find sub-agent sessions (have action_item_id) that weren't already rehydrated
    for (const session of flowSessions) {
      const actionItemId = session.action_item_id;

      // Skip if no action_item_id or already rehydrated
      if (!actionItemId || rehydratedSessionsRef.current.has(session.id)) {
        continue;
      }

      // Find the node with this action item — may be undefined for canvas_agent
      // items which are internal and never placed on canvas (orphan processes).
      const nodeId = actionItemToNodeMap.get(actionItemId);
      const anchorId = nodeId ?? `orphan-${actionItemId}`;

      // Create sub-agent process
      rehydratedSessionsRef.current.add(session.id);

      const processId = uuidv4();
      const label = flowSessionModeLabel(session.mode) || "Task execution";
      const newProcess: Process = {
        id: processId,
        label,
        status: mapFlowSessionStatus(session.status),
        flowSessionId: session.id,
        nodeId, // undefined for orphan processes
        createdAt: session.created_at
          ? new Date(session.created_at).getTime()
          : Date.now(),
        onClick: () => navigateToFlowSession(session.id),
      };

      // Add to anchor processes.
      // For node-anchored processes, mark existing ones as hidden from node
      // display (they still appear in the tray).
      const existingProcesses = nodeProcessesRef.current.get(anchorId) || [];
      const updatedExistingProcesses = nodeId
        ? existingProcesses.map((p) => ({ ...p, hiddenFromNode: true }))
        : existingProcesses;
      nodeProcessesRef.current.set(anchorId, [
        ...updatedExistingProcesses,
        newProcess,
      ]);

      // Store mapping
      sessionMapRef.current.set(session.id, { nodeId: anchorId, processId });

      // Populate actionItemNodeMapRef for WebSocket detection.
      // Only set for sessions with a real canvas node — the WebSocket handler
      // (Change A) computes the orphan anchor itself when nodeId is absent.
      if (nodeId) {
        actionItemNodeMapRef.current.set(actionItemId, nodeId);
      }

      hasChanges = true;
    }

    if (hasChanges) {
      triggerUpdate();
    }
  }, [flowSessions, canvasNodes, navigateToFlowSession, triggerUpdate]);

  // =============================================================================
  // Listen for out-of-band flow-session deletes (e.g. CanvasExplorer chats list).
  // Tears down: tray entry, persisted `processInfo` on the owning node, and
  // any `chatSession` node anchored to this flow session.
  // =============================================================================
  useEffect(() => {
    const handler = (e: Event) => {
      const { flowSessionId } = (e as CustomEvent).detail as {
        flowSessionId?: string;
      };
      if (!flowSessionId) return;
      const { canvas: l, canvasNodes: nodes } = canvasEnvRef.current;

      for (const node of nodes) {
        const data = node.data ?? {};
        const isAnchorChatSession =
          node.type === "chatSession" &&
          (data as { flowSessionId?: string }).flowSessionId === flowSessionId;
        if (isAnchorChatSession) {
          l.removeNode(node.id);
          continue;
        }
        const processInfo =
          (data as { processInfo?: PersistedProcessInfo[] }).processInfo ?? [];
        if (processInfo.some((p) => p.flowSessionId === flowSessionId)) {
          l.updateNodeData(node.id, {
            processInfo: processInfo.filter(
              (p) => p.flowSessionId !== flowSessionId,
            ),
          });
        }
      }

      const mapping = sessionMapRef.current.get(flowSessionId);
      if (mapping) {
        const processes = nodeProcessesRef.current.get(mapping.nodeId);
        if (processes) {
          const updated = processes.filter((p) => p.id !== mapping.processId);
          if (updated.length === 0) {
            nodeProcessesRef.current.delete(mapping.nodeId);
          } else {
            nodeProcessesRef.current.set(mapping.nodeId, updated);
          }
        }
        sessionMapRef.current.delete(flowSessionId);
      }
      // Drop the rehydration guard so a subsequent re-create with the same
      // id (highly unlikely, but possible during dev/HMR) can repopulate.
      rehydratedSessionsRef.current.delete(flowSessionId);
      triggerUpdate();
    };
    window.addEventListener(CANVAS_FLOW_SESSION_DELETED_EVENT, handler);
    return () =>
      window.removeEventListener(CANVAS_FLOW_SESSION_DELETED_EVENT, handler);
  }, [triggerUpdate]);

  // Update a specific process
  const updateProcess = useCallback(
    (nodeId: string, processId: string, updates: Partial<Process>) => {
      const processes = nodeProcessesRef.current.get(nodeId) || [];
      const updatedProcesses = processes.map((p) =>
        p.id === processId ? { ...p, ...updates } : p,
      );
      nodeProcessesRef.current.set(nodeId, updatedProcesses);
      triggerUpdate();
    },
    [triggerUpdate],
  );

  // Get processes for a node (excludes processes hidden from node display)
  const getProcessesForNode = useCallback((nodeId: string): Process[] => {
    const processes = nodeProcessesRef.current.get(nodeId) || [];
    return processes.filter((p) => !p.hiddenFromNode);
  }, []);

  // Check if any process is running for a node
  const isProcessing = useCallback((nodeId: string): boolean => {
    const processes = nodeProcessesRef.current.get(nodeId) || [];
    return processes.some(
      (p) => p.status === "pending" || p.status === "processing",
    );
  }, []);

  // Remove a process from the tray (dismisses it from the list and deletes the flow session via API)
  const removeProcess = useCallback(
    (processId: string) => {
      for (const [nodeId, processes] of nodeProcessesRef.current) {
        const process = processes.find((p) => p.id === processId);
        if (process) {
          if (process.flowSessionId) {
            sessionMapRef.current.delete(process.flowSessionId);
            deleteFlowSessionMutate(process.flowSessionId);
          }
          const updated = processes.filter((p) => p.id !== processId);
          if (updated.length === 0) {
            nodeProcessesRef.current.delete(nodeId);
          } else {
            nodeProcessesRef.current.set(nodeId, updated);
          }
          triggerUpdate();
          return;
        }
      }
    },
    [triggerUpdate, deleteFlowSessionMutate],
  );

  // Dispatch a new process
  const dispatchProcess = useCallback(
    async (params: DispatchProcessParams): Promise<string | undefined> => {
      const {
        nodeId,
        entity,
        referencedEntities: explicitRefs,
        action,
        customPrompt,
      } = params;
      const config = ACTION_CONFIG[action];

      if (!config) {
        console.error(`Unknown action: ${action}`);
        return undefined;
      }

      const processId = uuidv4();
      const entityData = entity ? getEntityData(entity) : null;

      // For execute_action_item, track the action item ID so we can detect sub-agent sessions
      if (action === "execute_action_item" && entityData?.id) {
        actionItemNodeMapRef.current.set(String(entityData.id), nodeId);
      }

      // Build referenced entities: explicit override takes priority, otherwise derive from entity
      const messageReferencedEntities: ReferencedEntity[] =
        explicitRefs ??
        (entity && entityData
          ? [
              {
                entity_id: String(entityData.id),
                entity_type: getEntityType(entity),
                name: getEntityName(entity),
                platform: entity.platform || "cohesive",
              },
            ]
          : []);

      // Use custom prompt if provided, otherwise use config prompt
      const messageContent = customPrompt || config.prompt;

      // Create initial process with pending status
      const newProcess: Process = {
        id: processId,
        label: config.label,
        status: "pending",
        nodeId,
        createdAt: Date.now(),
      };

      // Add process to node
      const existingProcesses = nodeProcessesRef.current.get(nodeId) || [];
      nodeProcessesRef.current.set(nodeId, [...existingProcesses, newProcess]);
      triggerUpdate();

      try {
        const { canvas: liveCanvas, canvasNodes: liveNodes } =
          canvasEnvRef.current;

        // Create flow session
        const flowSession = await createFlowSessionAsync({
          mode: config.mode,
          class: "canvas",
          canvas_id: canvasId,
        });

        const flowSessionId = flowSession.id;

        // Store mapping
        sessionMapRef.current.set(flowSessionId, { nodeId, processId });

        // Mark this session as already tracked to avoid rehydration duplication
        rehydratedSessionsRef.current.add(flowSessionId);

        // Persist process info to node data in Loro for rehydration after refresh
        const currentNode = liveNodes.find((n) => n.id === nodeId);
        const existingProcessInfo =
          (currentNode?.data?.processInfo as PersistedProcessInfo[]) || [];
        liveCanvas.updateNodeData(nodeId, {
          processInfo: [
            ...existingProcessInfo,
            { flowSessionId, label: config.label },
          ],
        });

        // Update to processing status and add onClick for navigation
        updateProcess(nodeId, processId, {
          status: "processing",
          flowSessionId,
          onClick: () => navigateToFlowSession(flowSessionId),
        });

        // Post message with referenced entities
        await postFlowMessageAsync({
          flowSessionId,
          id: uuidv4(),
          content: messageContent,
          referenced_entities: messageReferencedEntities,
        });

        // Status will be updated via WebSocket events (flow_session.updated)
        // User can click the process pill to open the session in right pane
        return flowSessionId;
      } catch (error) {
        console.error("Error dispatching process:", error);
        updateProcess(nodeId, processId, { status: "error" });
        return undefined;
      }
    },
    [
      canvasId,
      createFlowSessionAsync,
      postFlowMessageAsync,
      triggerUpdate,
      updateProcess,
      navigateToFlowSession,
    ],
  );

  // Compute all processes and active count
  // Note: version is used as a dependency to re-compute when triggerUpdate() is called
  const allProcesses = useMemo(() => {
    const all: Process[] = [];
    nodeProcessesRef.current.forEach((processes) => {
      all.push(...processes);
    });
    return all;
  }, [version]);

  const activeProcessCount = useMemo(() => {
    return allProcesses.filter(
      (p) =>
        p.status === "pending" ||
        p.status === "processing" ||
        p.status === "needs_attention",
    ).length;
  }, [allProcesses]);

  const value = useMemo<CanvasProcessContextType>(
    () => ({
      getProcessesForNode,
      dispatchProcess,
      isProcessing,
      allProcesses,
      activeProcessCount,
      isLoading: isLoadingFlowSessions,
      highlightedNodeIds,
      setHighlightedNodeIds,
      removeProcess,
    }),
    [
      getProcessesForNode,
      dispatchProcess,
      isProcessing,
      allProcesses,
      activeProcessCount,
      isLoadingFlowSessions,
      highlightedNodeIds,
      setHighlightedNodeIds,
      removeProcess,
    ],
  );

  return (
    <CanvasProcessContext.Provider value={value}>
      {children}
    </CanvasProcessContext.Provider>
  );
}
